import React, { useRef, useState } from "react";
import { Upload, X, Link as LinkIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Image upload with camera / gallery support (mobile-friendly).
 *
 * Flow:
 *   1. User taps → native file picker opens (on phones this offers camera + gallery).
 *   2. Client-side compress to keep upload small (~300 KB max).
 *   3. Upload compressed JPEG to backend (`POST /api/uploads/image`).
 *   4. Backend returns a URL we store in the field (`/api/files/…`).
 *
 * Also supports pasting an external URL via the "Pegar URL" toggle.
 *
 * Props:
 *  - value:    current image URL (or legacy base64 data URL)
 *  - onChange: (url: string) => void
 *  - label, hint
 *  - aspect: "square" | "wide"
 *  - purpose: "logo" | "cover" | "avatar"  (defaults to "logo")
 *  - allowUrl: boolean (default true) — show "Pegar URL" fallback
 */
const ImageUpload = ({
  value,
  onChange,
  label,
  hint,
  aspect = "square",
  purpose = "logo",
  allowUrl = true,
  maxSizeKb = 5120,
  positionY,
  onPositionYChange,
  testid = "image-upload",
}) => {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [urlMode, setUrlMode] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");

  const compressImage = (file, targetAspect) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const maxW = targetAspect === "wide" ? 1600 : 800;
          const maxH = targetAspect === "wide" ? 600 : 800;          let w = img.width;
          let h = img.height;
          const ratio = Math.min(maxW / w, maxH / h, 1);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          // Aim for ≤ 400 KB after compression
          let quality = 0.85;
          const toBlob = (q) =>
            new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
          (async () => {
            let blob = await toBlob(quality);
            while (blob && blob.size > 400 * 1024 && quality > 0.4) {
              quality -= 0.1;
              blob = await toBlob(quality);
            }
            if (!blob) return reject(new Error("compress failed"));
            resolve(blob);
          })();
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Solo imágenes (JPG, PNG, WebP)");
      return;
    }
    const sizeKb = file.size / 1024;
    if (sizeKb > maxSizeKb) {
      setError(`Imagen muy grande (${Math.round(sizeKb)}KB). Máximo ${maxSizeKb}KB.`);
      return;
    }
    setLoading(true);
    try {
      const blob = await compressImage(file, aspect);
      const fd = new FormData();
      fd.append("file", blob, "upload.jpg");
      const { data } = await api.post(`/uploads/image?purpose=${purpose}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "No se pudo subir la imagen. Intenta con otra.";
      setError(msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyUrl = () => {
    const v = urlDraft.trim();
    if (!v) return;
    onChange(v);
    setUrlDraft("");
    setUrlMode(false);
    setError("");
  };

  const aspectCls = aspect === "wide" ? "aspect-[2.7/1]" : "aspect-square w-32";

  return (
    <div data-testid={`${testid}-wrapper`}>
      {label && (
        <label className="text-xs font-bold uppercase tracking-wider text-teal-soft block mb-1">
          {label}
        </label>
      )}
      {hint && <p className="text-xs text-teal-soft mb-2">{hint}</p>}

      <div className="flex items-start gap-3">
        <div
          onClick={() => !loading && inputRef.current?.click()}
          className={`${aspectCls} ${aspect === "wide" ? "w-full" : ""} bg-cream rounded-2xl border-2 border-dashed border-gray-300 hover:border-orange transition-colors cursor-pointer overflow-hidden flex items-center justify-center relative group`}
          data-testid={`${testid}-dropzone`}
        >
          {loading ? (
            <div className="text-teal-soft text-center p-3 text-xs flex flex-col items-center gap-1">
              <Loader2 size={20} className="animate-spin" />
              <span>Subiendo…</span>
            </div>
          ) : value ? (
            <>
              <img
                src={value}
                alt=""
                className="w-full h-full object-cover"
                style={
                  aspect === "wide" && typeof positionY === "number"
                    ? { objectPosition: `center ${positionY}%` }
                    : undefined
                }
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                }}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center shadow"
                aria-label="Quitar imagen"
                data-testid={`${testid}-remove`}
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <div className="text-teal-soft text-center p-3">
              <Upload size={20} className="mx-auto mb-1" />
              <span className="text-xs font-semibold">Subir foto</span>
              <span className="block text-[10px] text-gray-400 mt-0.5">
                Cámara o galería · JPG/PNG/WEBP · máx 5 MB
              </span>
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = "";
          }}
          data-testid={testid}
        />
      </div>

      {error && (
        <p className="text-xs text-red-600 mt-1" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}

      {aspect === "wide" && value && typeof positionY === "number" && onPositionYChange && (
        <div className="mt-3" data-testid={`${testid}-position`}>
          <label className="flex items-center justify-between text-xs font-semibold text-teal-soft mb-1">
            <span>Ajustar posición vertical</span>
            <span className="text-teal-deep tabular-nums">{positionY}%</span>
          </label>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={positionY}
            onChange={(e) => onPositionYChange(Number(e.target.value))}
            className="w-full accent-orange"
            data-testid={`${testid}-position-slider`}
          />
          <p className="text-[10px] text-gray-400 mt-0.5">
            Arriba (0%) — Centro (50%) — Abajo (100%)
          </p>
        </div>
      )}

      {allowUrl && (
        <div className="mt-2">
          {urlMode ? (
            <div className="flex items-center gap-2">
              <input
                type="url"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                placeholder="https://…"
                className="field-input flex-1 text-xs"
                data-testid={`${testid}-url-input`}
              />
              <button
                type="button"
                onClick={applyUrl}
                className="text-xs px-3 py-2 rounded-lg bg-teal-deep text-white hover:bg-teal"
                data-testid={`${testid}-url-apply`}
              >
                Usar
              </button>
              <button
                type="button"
                onClick={() => {
                  setUrlMode(false);
                  setUrlDraft("");
                }}
                className="text-xs px-2 py-2 rounded-lg text-teal-soft hover:text-orange"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setUrlMode(true)}
              className="text-xs text-teal-soft hover:text-orange inline-flex items-center gap-1"
              data-testid={`${testid}-toggle-url`}
            >
              <LinkIcon size={12} /> o pegar una URL de imagen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
